import { toast } from 'sonner';
import { AdditionalOption, DropdownChoice } from '../types';

export interface OptionFormData {
  label: string;
  type: 'select' | 'checkbox' | 'text';
  required: boolean;
  options: DropdownChoice[];
}

/**
 * Validate option form data before submission
 */
export const validateOptionForm = (formData: OptionFormData): { isValid: boolean; error?: string } => {
  // Check if label is provided
  if (!formData.label.trim()) {
    return { isValid: false, error: 'Please enter an option name' };
  }

  // Check label length
  if (formData.label.trim().length < 2) {
    return { isValid: false, error: 'Option name must be at least 2 characters long' };
  }

  if (formData.label.trim().length > 50) {
    return { isValid: false, error: 'Option name must be less than 50 characters' };
  }

  // For select type, validate choices
  if (formData.type === 'select') {
    if (formData.options.length < 2) {
      return { isValid: false, error: 'Select options must have at least 2 choices' };
    }

    // Check for empty or duplicate choices
    const choiceValues = formData.options.map(opt => opt.value.trim());
    const emptyChoices = choiceValues.filter(value => !value);
    
    if (emptyChoices.length > 0) {
      return { isValid: false, error: 'All choice values must be filled' };
    }

    const uniqueValues = new Set(choiceValues);
    if (uniqueValues.size !== choiceValues.length) {
      return { isValid: false, error: 'Choice values must be unique' };
    }
  }

  return { isValid: true };
};

/**
 * Convert form data to AdditionalOption format
 */
export const convertFormToOption = (formData: OptionFormData): AdditionalOption => {
  return {
    id: Date.now().toString(),
    label: formData.label.trim(),
    type: formData.type,
    required: formData.required,
    options: formData.type === 'select' ? formData.options : undefined,
    pricing: []
  };
};

/**
 * Get default form data for new option
 */
export const getDefaultFormData = (): OptionFormData => ({
  label: '',
  type: 'select',
  required: false,
  options: [
    { value: 'Option 1', pricing: [] },
    { value: 'Option 2', pricing: [] }
  ]
});

/**
 * Validate and submit option with proper error handling
 */
export const validateAndSubmitOption = (
  formData: OptionFormData,
  onAddOption: (option: AdditionalOption) => void,
  onSuccess?: () => void
): boolean => {
  const validation = validateOptionForm(formData);
  
  if (!validation.isValid) {
    toast.error(validation.error);
    return false;
  }

  try {
    const option = convertFormToOption(formData);
    onAddOption(option);
    toast.success('Option added successfully');
    onSuccess?.();
    return true;
  } catch (error) {
    toast.error('Failed to add option. Please try again.');
    return false;
  }
};
